import { useState, useCallback } from 'react';
import { db } from '../lib/db';
import { v4 as uuidv4 } from 'uuid';
import bcrypt from 'bcryptjs';

// Users Hook
export function useUsers() {
  const [loading, setLoading] = useState(false);

  const getUsers = useCallback(async () => {
    return await db.users.toArray();
  }, []);

  const getUserById = useCallback(async (id) => {
    return await db.users.get(id);
  }, []);

  const addUser = useCallback(async (userData) => {
    setLoading(true);
    try {
      const passwordHash = await bcrypt.hash(userData.password || 'Employee@123', 10);
      const user = {
        id: uuidv4(),
        email: userData.email,
        passwordHash,
        role: userData.role || 'employee',
        adminId: userData.adminId,
        supervisorId: userData.supervisorId,
        profile: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          photo: '',
          department: userData.department,
          position: userData.position,
          location: userData.location || 'HQ',
          phone: userData.phone || '',
          dateOfBirth: userData.dateOfBirth || Date.now(),
          hireDate: userData.hireDate || Date.now(),
          gender: userData.gender || '',
          workState: userData.workState || '',
          residentState: userData.residentState || ''
        },
        status: 'active',
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      await db.users.add(user);
      return user;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback(async (id, updates) => {
    setLoading(true);
    try {
      await db.users.update(id, { ...updates, updatedAt: Date.now() });
      return await db.users.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteUser = useCallback(async (id) => {
    await db.users.update(id, { status: 'deleted', updatedAt: Date.now() });
  }, []);

  return { getUsers, getUserById, addUser, updateUser, deleteUser, loading };
}

// Appraisals Hook
export function useAppraisals() {
  const [loading, setLoading] = useState(false);

  const getAppraisals = useCallback(async () => {
    return await db.appraisals.toArray();
  }, []);

  const getAppraisalById = useCallback(async (id) => {
    return await db.appraisals.get(id);
  }, []);

  const createAppraisal = useCallback(async (appraisalData) => {
    setLoading(true);
    try {
      const appraisal = {
        id: uuidv4(),
        templateId: appraisalData.templateId,
        userId: appraisalData.userId,
        managerId: appraisalData.managerId,
        title: appraisalData.title,
        type: appraisalData.type || 'annual',
        status: appraisalData.status || 'ready_to_evaluate',
        dueDate: appraisalData.dueDate,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        finalizedAt: appraisalData.finalizedAt ?? null,
        employeeSubmittedAt: appraisalData.employeeSubmittedAt ?? null,
        managerSubmittedAt: appraisalData.managerSubmittedAt ?? null,
        performanceAreas: appraisalData.performanceAreas || [],
        goals: { current: [], future: [], pdp: [] },
        comments: {},
        progress: { performanceAreasComplete: false, goalsComplete: false, commentsComplete: false, overallPercent: 0 },
        scores: {},
        cycleDays: appraisalData.cycleDays ?? null,
        autoReminders: appraisalData.autoReminders ?? false,
        reminderLeadDays: appraisalData.reminderLeadDays ?? null,
        remindersSent: appraisalData.remindersSent ?? false,
        visibleToSupervisor: appraisalData.visibleToSupervisor !== false,
        // CEO-related fields
        assignedByCeo: appraisalData.assignedByCeo ?? null,
        ceoId: appraisalData.ceoId ?? null
      };
      await db.appraisals.add(appraisal);
      return appraisal;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAppraisal = useCallback(async (id, updates) => {
    setLoading(true);
    try {
      await db.appraisals.update(id, { ...updates, updatedAt: Date.now() });
      return await db.appraisals.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAppraisal = useCallback(async (id) => {
    await db.appraisals.delete(id);
  }, []);

  return { getAppraisals, getAppraisalById, createAppraisal, updateAppraisal, deleteAppraisal, loading };
}

// Templates Hook
export function useTemplates() {
  const [loading, setLoading] = useState(false);

  const getTemplates = useCallback(async () => {
    return await db.templates.toArray();
  }, []);

  const getTemplateById = useCallback(async (id) => {
    return await db.templates.get(id);
  }, []);

  const createTemplate = useCallback(async (templateData) => {
    setLoading(true);
    try {
      const template = {
        id: uuidv4(),
        name: templateData.name,
        description: templateData.description,
        type: templateData.type,
        sections: templateData.sections || [],
        settings: templateData.settings || {},
        createdBy: templateData.createdBy,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      await db.templates.add(template);
      return template;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTemplate = useCallback(async (id, updates) => {
    setLoading(true);
    try {
      await db.templates.update(id, { ...updates, updatedAt: Date.now() });
      return await db.templates.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTemplate = useCallback(async (id) => {
    await db.templates.delete(id);
  }, []);

  return { getTemplates, getTemplateById, createTemplate, updateTemplate, deleteTemplate, loading };
}

// Calendar Events Hook
export function useCalendarEvents() {
  const [loading, setLoading] = useState(false);

  const getEvents = useCallback(async () => {
    return await db.calendarEvents.toArray();
  }, []);

  const createEvent = useCallback(async (eventData) => {
    setLoading(true);
    try {
      const event = {
        id: uuidv4(),
        title: eventData.title,
        description: eventData.description || '',
        type: eventData.type || 'meeting',
        startDate: eventData.startDate,
        endDate: eventData.endDate || eventData.startDate,
        allDay: eventData.allDay || false,
        participants: eventData.participants || [],
        location: eventData.location || '',
        meetingLink: eventData.meetingLink || '',
        createdBy: eventData.createdBy,
        createdAt: Date.now()
      };
      await db.calendarEvents.add(event);
      return event;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteEvent = useCallback(async (id) => {
    await db.calendarEvents.delete(id);
  }, []);

  return { getEvents, createEvent, deleteEvent, loading };
}

// Invites Hook
export function useInvites() {
  const [loading, setLoading] = useState(false);

  const getInvites = useCallback(async () => {
    return await db.invites.toArray();
  }, []);

  const createInvite = useCallback(async (inviteData) => {
    setLoading(true);
    try {
      const invite = {
        id: uuidv4(),
        inviteCode: `INV-${new Date().getFullYear()}-${uuidv4().slice(0, 8).toUpperCase()}`,
        role: inviteData.role,
        createdBy: inviteData.createdBy,
        expiryDate: inviteData.expiryDate,
        maxUses: inviteData.maxUses || 10,
        usedCount: 0,
        department: inviteData.department || '',
        position: inviteData.position || '',
        supervisorId: inviteData.supervisorId || null,
        metadata: { allowMultipleUses: true, requiresApproval: false },
        status: 'active',
        createdAt: Date.now()
      };
      await db.invites.add(invite);
      return invite;
    } finally {
      setLoading(false);
    }
  }, []);

  const deactivateInvite = useCallback(async (id) => {
    await db.invites.update(id, { status: 'expired' });
  }, []);

  return { getInvites, createInvite, deactivateInvite, loading };
}

// Goals Hook
export function useGoals() {
  const [loading, setLoading] = useState(false);

  const getGoals = useCallback(async () => {
    return await db.goals.toArray();
  }, []);

  const createGoal = useCallback(async (goalData) => {
    setLoading(true);
    try {
      const goal = {
        id: uuidv4(),
        userId: goalData.userId,
        appraisalId: goalData.appraisalId || null,
        title: goalData.title,
        description: goalData.description || '',
        status: goalData.status || 'not_started',
        priority: goalData.priority || 'medium',
        type: goalData.type || 'current',
        dueDate: goalData.dueDate,
        completedDate: null,
        employeeComment: '',
        managerComment: '',
        rolloverToNext: false,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      await db.goals.add(goal);
      return goal;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateGoal = useCallback(async (id, updates) => {
    setLoading(true);
    try {
      await db.goals.update(id, { ...updates, updatedAt: Date.now() });
      return await db.goals.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteGoal = useCallback(async (id) => {
    await db.goals.delete(id);
  }, []);

  return { getGoals, createGoal, updateGoal, deleteGoal, loading };
}

// Feedback Hook
export function useFeedbacks() {
  const [loading, setLoading] = useState(false);

  const getFeedbacks = useCallback(async () => {
    return await db.feedbacks.toArray();
  }, []);

  const getFeedbacksForUser = useCallback(async (userId) => {
    return await db.feedbacks.where('receiverId').equals(userId).reverse().toArray();
  }, []);

  const createFeedback = useCallback(async (feedbackData) => {
    setLoading(true);
    try {
      const feedback = {
        id: uuidv4(),
        senderId: feedbackData.senderId,
        senderRole: feedbackData.senderRole,
        receiverId: feedbackData.receiverId,
        receiverRole: feedbackData.receiverRole,
        anonymous: feedbackData.anonymous || false,
        type: feedbackData.type || 'general',
        message: feedbackData.message,
        department: feedbackData.department || '',
        createdAt: Date.now()
      };
      await db.feedbacks.add(feedback);
      return feedback;
    } finally {
      setLoading(false);
    }
  }, []);

  return { getFeedbacks, getFeedbacksForUser, createFeedback, loading };
}

// Notifications Hook
export function useNotifications() {
  const [loading, setLoading] = useState(false);

  const getNotifications = useCallback(async (userId) => {
    return await db.notifications.where('userId').equals(userId).toArray();
  }, []);

  const createNotification = useCallback(async (notificationData) => {
    const notification = {
      id: uuidv4(),
      userId: notificationData.userId,
      type: notificationData.type,
      title: notificationData.title,
      message: notificationData.message,
      link: notificationData.link || '',
      read: false,
      actionRequired: notificationData.actionRequired || false,
      priority: notificationData.priority || 'medium',
      timestamp: Date.now()
    };
    await db.notifications.add(notification);
    return notification;
  }, []);

  const markAsRead = useCallback(async (id) => {
    await db.notifications.update(id, { read: true });
  }, []);

  const clearNotifications = useCallback(async (userId) => {
    await db.notifications.where('userId').equals(userId).delete();
  }, []);

  return { getNotifications, createNotification, markAsRead, clearNotifications, loading };
}

// ============ PAYROLL HOOKS ============

// Payroll Settings Hook
export function usePayrollSettings() {
  const [loading, setLoading] = useState(false);

  const getSettings = useCallback(async () => {
    const settings = await db.payrollSettings.toArray();
    return settings[0] || null;
  }, []);

  const updateSettings = useCallback(async (id, updates) => {
    setLoading(true);
    try {
      await db.payrollSettings.update(id, { ...updates, updatedAt: Date.now() });
      return await db.payrollSettings.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  return { getSettings, updateSettings, loading };
}

// Salary Structures Hook
export function useSalaryStructures() {
  const [loading, setLoading] = useState(false);

  const getSalaryStructures = useCallback(async () => {
    return await db.salaryStructures.toArray();
  }, []);

  const getSalaryByUserId = useCallback(async (userId) => {
    return await db.salaryStructures.where('userId').equals(userId).first();
  }, []);

  const createSalaryStructure = useCallback(async (data) => {
    setLoading(true);
    try {
      const structure = {
        id: uuidv4(),
        userId: data.userId,
        basicSalary: data.basicSalary,
        hra: data.hra || 0,
        transportAllowance: data.transportAllowance || 0,
        medicalAllowance: data.medicalAllowance || 0,
        specialAllowance: data.specialAllowance || 0,
        grossSalary: data.basicSalary + (data.hra || 0) + (data.transportAllowance || 0) + (data.medicalAllowance || 0) + (data.specialAllowance || 0),
        pfRate: data.pfRate || 12,
        professionalTax: data.professionalTax || 200,
        loanEmi: data.loanEmi || 0,
        otherDeductions: data.otherDeductions || 0,
        effectiveDate: data.effectiveDate || Date.now(),
        status: 'active',
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      await db.salaryStructures.add(structure);
      return structure;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSalaryStructure = useCallback(async (id, updates) => {
    setLoading(true);
    try {
      const gross = (updates.basicSalary || 0) + (updates.hra || 0) + (updates.transportAllowance || 0) + (updates.medicalAllowance || 0) + (updates.specialAllowance || 0);
      await db.salaryStructures.update(id, { 
        ...updates, 
        grossSalary: gross,
        pfRate: updates.pfRate || 12,
        professionalTax: updates.professionalTax || 200,
        loanEmi: updates.loanEmi || 0,
        otherDeductions: updates.otherDeductions || 0,
        updatedAt: Date.now() 
      });
      return await db.salaryStructures.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  return { getSalaryStructures, getSalaryByUserId, createSalaryStructure, updateSalaryStructure, loading };
}

// Payroll Periods Hook
export function usePayrollPeriods() {
  const [loading, setLoading] = useState(false);

  const getPeriods = useCallback(async () => {
    return await db.payrollPeriods.toArray();
  }, []);

  const getPeriodById = useCallback(async (id) => {
    return await db.payrollPeriods.get(id);
  }, []);

  const createPeriod = useCallback(async (data) => {
    setLoading(true);
    try {
      const period = {
        id: uuidv4(),
        periodName: data.periodName,
        month: data.month,
        year: data.year,
        startDate: data.startDate,
        endDate: data.endDate,
        status: 'draft',
        processedAt: null,
        processedBy: null,
        totalGross: 0,
        totalDeductions: 0,
        totalNet: 0,
        employeeCount: 0,
        createdAt: Date.now()
      };
      await db.payrollPeriods.add(period);
      return period;
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePeriod = useCallback(async (id, updates) => {
    setLoading(true);
    try {
      await db.payrollPeriods.update(id, updates);
      return await db.payrollPeriods.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  return { getPeriods, getPeriodById, createPeriod, updatePeriod, loading };
}

// Payslips Hook
export function usePayslips() {
  const [loading, setLoading] = useState(false);

  const getPayslips = useCallback(async () => {
    return await db.payslips.toArray();
  }, []);

  const getPayslipsByUserId = useCallback(async (userId) => {
    return await db.payslips.where('userId').equals(userId).reverse().toArray();
  }, []);

  const getPayslipsByPeriod = useCallback(async (periodId) => {
    return await db.payslips.where('periodId').equals(periodId).toArray();
  }, []);

  const getPayslipById = useCallback(async (id) => {
    return await db.payslips.get(id);
  }, []);

  const createPayslip = useCallback(async (data) => {
    setLoading(true);
    try {
      const payslip = {
        id: uuidv4(),
        userId: data.userId,
        periodId: data.periodId,
        periodName: data.periodName,
        month: data.month,
        year: data.year,
        earnings: data.earnings || {},
        deductions: data.deductions || {},
        grossEarnings: data.grossEarnings || 0,
        totalDeductions: data.totalDeductions || 0,
        netPay: data.netPay || 0,
        workingDays: data.workingDays || 22,
        daysWorked: data.daysWorked || 22,
        lop: data.lop || 0,
        status: 'generated',
        generatedAt: Date.now(),
        paidAt: null,
        paymentMethod: data.paymentMethod || 'bank_transfer',
        createdAt: Date.now()
      };
      await db.payslips.add(payslip);
      return payslip;
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePayslip = useCallback(async (id, updates) => {
    setLoading(true);
    try {
      await db.payslips.update(id, updates);
      return await db.payslips.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  return { getPayslips, getPayslipsByUserId, getPayslipsByPeriod, getPayslipById, createPayslip, updatePayslip, loading };
}

// Earning Types Hook
export function useEarningTypes() {
  const [loading, setLoading] = useState(false);

  const getEarningTypes = useCallback(async () => {
    return await db.earningTypes.toArray();
  }, []);

  const createEarningType = useCallback(async (data) => {
    setLoading(true);
    try {
      const type = {
        id: uuidv4(),
        name: data.name,
        code: data.code,
        category: data.category || 'fixed',
        isTaxable: data.isTaxable !== false,
        isActive: true,
        description: data.description || '',
        createdAt: Date.now()
      };
      await db.earningTypes.add(type);
      return type;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateEarningType = useCallback(async (id, updates) => {
    await db.earningTypes.update(id, updates);
    return await db.earningTypes.get(id);
  }, []);

  return { getEarningTypes, createEarningType, updateEarningType, loading };
}

// Deduction Types Hook
export function useDeductionTypes() {
  const [loading, setLoading] = useState(false);

  const getDeductionTypes = useCallback(async () => {
    return await db.deductionTypes.toArray();
  }, []);

  const createDeductionType = useCallback(async (data) => {
    setLoading(true);
    try {
      const type = {
        id: uuidv4(),
        name: data.name,
        code: data.code,
        category: data.category || 'voluntary',
        isStatutory: data.isStatutory || false,
        isActive: true,
        description: data.description || '',
        createdAt: Date.now()
      };
      await db.deductionTypes.add(type);
      return type;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateDeductionType = useCallback(async (id, updates) => {
    await db.deductionTypes.update(id, updates);
    return await db.deductionTypes.get(id);
  }, []);

  return { getDeductionTypes, createDeductionType, updateDeductionType, loading };
}

// Tax Slabs Hook
export function useTaxSlabs() {
  const [loading, setLoading] = useState(false);

  const getTaxSlabs = useCallback(async () => {
    const slabs = await db.taxSlabs.toArray();
    return slabs.sort((a, b) => a.minIncome - b.minIncome);
  }, []);

  const createTaxSlab = useCallback(async (data) => {
    setLoading(true);
    try {
      const slab = {
        id: uuidv4(),
        minIncome: data.minIncome,
        maxIncome: data.maxIncome,
        rate: data.rate,
        fiscalYear: data.fiscalYear || new Date().getFullYear().toString(),
        description: data.description || '',
        createdAt: Date.now()
      };
      await db.taxSlabs.add(slab);
      return slab;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTaxSlab = useCallback(async (id, updates) => {
    await db.taxSlabs.update(id, updates);
    return await db.taxSlabs.get(id);
  }, []);

  const deleteTaxSlab = useCallback(async (id) => {
    await db.taxSlabs.delete(id);
  }, []);

  return { getTaxSlabs, createTaxSlab, updateTaxSlab, deleteTaxSlab, loading };
}

// Bonuses Hook
export function useBonuses() {
  const [loading, setLoading] = useState(false);

  const getBonuses = useCallback(async () => {
    return await db.bonuses.toArray();
  }, []);

  const getBonusesByUserId = useCallback(async (userId) => {
    return await db.bonuses.where('userId').equals(userId).toArray();
  }, []);

  const createBonus = useCallback(async (data) => {
    setLoading(true);
    try {
      const bonus = {
        id: uuidv4(),
        userId: data.userId,
        type: data.type || 'performance',
        amount: data.amount,
        reason: data.reason || '',
        periodId: data.periodId || null,
        status: 'pending',
        approvedBy: null,
        approvedAt: null,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      await db.bonuses.add(bonus);
      return bonus;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveBonus = useCallback(async (id, approvedBy) => {
    setLoading(true);
    try {
      await db.bonuses.update(id, { 
        status: 'approved', 
        approvedBy, 
        approvedAt: Date.now(),
        updatedAt: Date.now()
      });
      return await db.bonuses.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectBonus = useCallback(async (id, reason) => {
    setLoading(true);
    try {
      await db.bonuses.update(id, { 
        status: 'rejected', 
        rejectionReason: reason,
        updatedAt: Date.now()
      });
      return await db.bonuses.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  return { getBonuses, getBonusesByUserId, createBonus, approveBonus, rejectBonus, loading };
}

// Reimbursements Hook
export function useReimbursements() {
  const [loading, setLoading] = useState(false);

  const getReimbursements = useCallback(async () => {
    return await db.reimbursements.toArray();
  }, []);

  const getReimbursementsByUserId = useCallback(async (userId) => {
    return await db.reimbursements.where('userId').equals(userId).toArray();
  }, []);

  const createReimbursement = useCallback(async (data) => {
    setLoading(true);
    try {
      const reimbursement = {
        id: uuidv4(),
        userId: data.userId,
        category: data.category,
        amount: data.amount,
        description: data.description || '',
        receiptUrl: data.receiptUrl || '',
        status: 'pending',
        submittedAt: Date.now(),
        approvedBy: null,
        approvedAt: null,
        createdAt: Date.now()
      };
      await db.reimbursements.add(reimbursement);
      return reimbursement;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveReimbursement = useCallback(async (id, approvedBy) => {
    setLoading(true);
    try {
      await db.reimbursements.update(id, { 
        status: 'approved', 
        approvedBy, 
        approvedAt: Date.now()
      });
      return await db.reimbursements.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectReimbursement = useCallback(async (id, reason) => {
    setLoading(true);
    try {
      await db.reimbursements.update(id, { 
        status: 'rejected', 
        rejectionReason: reason
      });
      return await db.reimbursements.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  return { getReimbursements, getReimbursementsByUserId, createReimbursement, approveReimbursement, rejectReimbursement, loading };
}

// Leave Balances Hook
export function useLeaveBalances() {
  const [loading, setLoading] = useState(false);

  const getLeaveBalances = useCallback(async () => {
    return await db.leaveBalances.toArray();
  }, []);

  const getLeaveBalancesByUserId = useCallback(async (userId) => {
    return await db.leaveBalances.where('userId').equals(userId).toArray();
  }, []);

  const updateLeaveBalance = useCallback(async (id, updates) => {
    await db.leaveBalances.update(id, updates);
    return await db.leaveBalances.get(id);
  }, []);

  return { getLeaveBalances, getLeaveBalancesByUserId, updateLeaveBalance, loading };
}

// Leave Requests Hook
export function useLeaveRequests() {
  const [loading, setLoading] = useState(false);

  const getLeaveRequests = useCallback(async () => {
    return await db.leaveRequests.toArray();
  }, []);

  const getLeaveRequestsByUserId = useCallback(async (userId) => {
    return await db.leaveRequests.where('userId').equals(userId).toArray();
  }, []);

  const createLeaveRequest = useCallback(async (data) => {
    setLoading(true);
    try {
      const days = Math.ceil((data.endDate - data.startDate) / (24 * 60 * 60 * 1000)) + 1;
      const request = {
        id: uuidv4(),
        userId: data.userId,
        leaveType: data.leaveType,
        startDate: data.startDate,
        endDate: data.endDate,
        days,
        reason: data.reason || '',
        status: 'pending',
        approvedBy: null,
        approvedAt: null,
        createdAt: Date.now()
      };
      await db.leaveRequests.add(request);
      return request;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveLeaveRequest = useCallback(async (id, approvedBy) => {
    setLoading(true);
    try {
      const request = await db.leaveRequests.get(id);
      await db.leaveRequests.update(id, { 
        status: 'approved', 
        approvedBy, 
        approvedAt: Date.now()
      });
      // Update leave balance
      const balances = await db.leaveBalances.where('userId').equals(request.userId).toArray();
      const balance = balances.find(b => b.leaveType === request.leaveType);
      if (balance) {
        await db.leaveBalances.update(balance.id, {
          used: balance.used + request.days,
          balance: balance.balance - request.days
        });
      }
      return await db.leaveRequests.get(id);
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectLeaveRequest = useCallback(async (id, reason) => {
    await db.leaveRequests.update(id, { 
      status: 'rejected', 
      rejectionReason: reason
    });
    return await db.leaveRequests.get(id);
  }, []);

  return { getLeaveRequests, getLeaveRequestsByUserId, createLeaveRequest, approveLeaveRequest, rejectLeaveRequest, loading };
}

// Attendance Records Hook
export function useAttendance() {
  const [loading, setLoading] = useState(false);

  const getAttendanceRecords = useCallback(async () => {
    return await db.attendanceRecords.toArray();
  }, []);

  const getAttendanceByUserId = useCallback(async (userId) => {
    return await db.attendanceRecords.where('userId').equals(userId).toArray();
  }, []);

  const createAttendanceRecord = useCallback(async (data) => {
    setLoading(true);
    try {
      const record = {
        id: uuidv4(),
        userId: data.userId,
        date: data.date,
        checkIn: data.checkIn,
        checkOut: data.checkOut,
        status: data.status || 'present',
        workHours: data.workHours || 0,
        overtime: data.overtime || 0,
        createdAt: Date.now()
      };
      await db.attendanceRecords.add(record);
      return record;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAttendanceRecord = useCallback(async (id, updates) => {
    await db.attendanceRecords.update(id, updates);
    return await db.attendanceRecords.get(id);
  }, []);

  return { getAttendanceRecords, getAttendanceByUserId, createAttendanceRecord, updateAttendanceRecord, loading };
}

// Loans Hook
export function useLoans() {
  const [loading, setLoading] = useState(false);

  const getLoans = useCallback(async () => {
    return await db.loans.toArray();
  }, []);

  const getLoansByUserId = useCallback(async (userId) => {
    return await db.loans.where('userId').equals(userId).toArray();
  }, []);

  const createLoan = useCallback(async (data) => {
    setLoading(true);
    try {
      const loan = {
        id: uuidv4(),
        userId: data.userId,
        loanType: data.loanType || 'personal',
        amount: data.amount,
        emiAmount: data.emiAmount,
        tenure: data.tenure,
        remainingAmount: data.amount,
        paidEmis: 0,
        remainingEmis: data.tenure,
        interestRate: data.interestRate || 0,
        status: 'pending',
        startDate: data.startDate,
        endDate: data.endDate,
        approvedBy: null,
        approvedAt: null,
        createdAt: Date.now()
      };
      await db.loans.add(loan);
      return loan;
    } finally {
      setLoading(false);
    }
  }, []);

  const approveLoan = useCallback(async (id, approvedBy) => {
    await db.loans.update(id, { 
      status: 'active', 
      approvedBy, 
      approvedAt: Date.now()
    });
    return await db.loans.get(id);
  }, []);

  const updateLoan = useCallback(async (id, updates) => {
    await db.loans.update(id, updates);
    return await db.loans.get(id);
  }, []);

  return { getLoans, getLoansByUserId, createLoan, approveLoan, updateLoan, loading };
}

// Bank Details Hook
export function useBankDetails() {
  const [loading, setLoading] = useState(false);

  const getBankDetails = useCallback(async () => {
    return await db.bankDetails.toArray();
  }, []);

  const getBankDetailsByUserId = useCallback(async (userId) => {
    return await db.bankDetails.where('userId').equals(userId).first();
  }, []);

  const createBankDetails = useCallback(async (data) => {
    setLoading(true);
    try {
      const details = {
        id: uuidv4(),
        userId: data.userId,
        accountNumber: data.accountNumber,
        bankName: data.bankName,
        ifscCode: data.ifscCode,
        accountType: data.accountType || 'Savings',
        isPrimary: true,
        createdAt: Date.now()
      };
      await db.bankDetails.add(details);
      return details;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateBankDetails = useCallback(async (id, updates) => {
    await db.bankDetails.update(id, updates);
    return await db.bankDetails.get(id);
  }, []);

  return { getBankDetails, getBankDetailsByUserId, createBankDetails, updateBankDetails, loading };
}
